import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, IconButton, useTheme, Divider, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { Toast } from '../../context/toastContext';
import { useTranslation } from 'react-i18next';

interface ToastNotificationProps {
  toast: Toast;
  isPaused: boolean;
  onClose: () => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ toast, isPaused, onClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const isDark = theme.palette.mode === 'dark';
  const remainingTimeRef = useRef<number>(toast.duration || 0);
  const animationFrameRef = useRef<number>();
  const lastTimeRef = useRef<number>(Date.now());

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  }, [onClose]);

  // Progress bar animation and auto-dismiss logic
  useEffect(() => {
    // Don't auto-dismiss if duration is 0 or undefined
    if (!toast.duration || toast.duration <= 0) {
      return;
    }

    let isActive = true;
    lastTimeRef.current = Date.now();
    
    const animate = () => {
      if (!isActive) return;
      
      const now = Date.now();
      const deltaTime = now - lastTimeRef.current;
      lastTimeRef.current = now;
      
      if (!isPaused) {
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - deltaTime);
        const progressPercentage = (remainingTimeRef.current / toast.duration!) * 100;
        setProgress(progressPercentage);
        
        if (remainingTimeRef.current <= 0) {
          isActive = false;
          handleClose();
          return;
        }
      }
      
      if (isActive) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      isActive = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [toast.duration, isPaused, handleClose]);

  const getToastConfig = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          bg: theme.palette.background.green,
          bgOpacity: isDark ? theme.palette.background.sidebar : theme.palette.background.white,
          border: theme.palette.border.green,
          iconColor: isDark ? theme.palette.success.light : theme.palette.success.dark,
          textColor: isDark ? theme.palette.text.dark : theme.palette.text.primary,
          progressColor: theme.palette.border.green,
        };
      case 'error':
        return {
          icon: ErrorIcon,
          bg: theme.palette.background.red,
          bgOpacity: isDark ? theme.palette.background.sidebar : theme.palette.background.white,
          border: theme.palette.background.red,
          iconColor: isDark ? theme.palette.error.light : theme.palette.error.dark,
          textColor: isDark ? theme.palette.text.dark : theme.palette.text.primary,
          progressColor: theme.palette.background.red,
        };
      case 'warning':
        return {
          icon: WarningIcon,
          bg: theme.palette.background.orange,
          bgOpacity: isDark ? theme.palette.background.sidebar : theme.palette.background.white,
          border: theme.palette.background.orange,
          iconColor: isDark ? theme.palette.warning.light : theme.palette.warning.dark,
          textColor: isDark ? theme.palette.text.dark : theme.palette.text.primary,
          progressColor: theme.palette.background.orange,
        };
      case 'info':
      default:
        return {
          icon: InfoIcon,
          bg: theme.palette.border.purple,
          bgOpacity: isDark ? theme.palette.background.sidebar : theme.palette.background.white,
          border: theme.palette.border.purple,
          iconColor: isDark ? theme.palette.info.light : theme.palette.info.dark,
          textColor: isDark ? theme.palette.text.dark : theme.palette.text.primary,
          progressColor: theme.palette.border.purple,
        };
    }
  };

  const config = getToastConfig();
  const Icon = config.icon;

  return (
    <Box
      sx={{
        position: 'relative',
        pointerEvents: 'auto',
        display: 'flex',
        gap: 2,
        padding: '18px 20px',
        paddingBottom: toast.duration && toast.duration > 0 ? '14px' : '18px',
        paddingRight: '48px',
        minHeight: '90px',
        backgroundColor: config.bgOpacity,
        borderLeft: `4px solid ${config.border}`,
        borderRadius: '12px',
        boxShadow: isDark
          ? '0 10px 40px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
          : '0 10px 40px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06)',
        backdropFilter: 'blur(10px)',
        animation: isExiting
          ? 'slideOutRight 0.3s ease-in-out forwards'
          : 'slideInRight 0.3s ease-in-out',
        '@keyframes slideInRight': {
          from: {
            transform: 'translateX(100%)',
            opacity: 0,
          },
          to: {
            transform: 'translateX(0)',
            opacity: 1,
          },
        },
        '@keyframes slideOutRight': {
          from: {
            transform: 'translateX(0)',
            opacity: 1,
          },
          to: {
            transform: 'translateX(100%)',
            opacity: 0,
          },
        },
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: isDark
            ? '0 14px 48px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4)'
            : '0 14px 48px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <Icon
        sx={{
          fontSize: '28px',
          color: config.iconColor,
          flexShrink: 0,
          mt: 0.25,
        }}
      />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.4,
            color: config.textColor,
            wordBreak: 'break-word',
          }}
        >
          {toast.message}
        </Box>
        <Divider 
          sx={{ 
            borderColor: config.border,
            opacity: 0.3,
            my: 0.5,
          }} 
        />
        <Box
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            fontStyle: 'italic',
            lineHeight: 1.3,
            color: config.textColor,
            opacity: 0.6,
            minHeight: '16px',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {toast.action ? (
            <Button
              size="small"
              variant="outlined"
              onClick={toast.action.onClick}
              sx={{
                minHeight: '24px',
                fontSize: '11px',
                padding: '2px 8px',
                borderColor: config.border,
                color: config.textColor,
                '&:hover': {
                  borderColor: config.iconColor,
                  backgroundColor: config.bg,
                },
              }}
            >
              {toast.action.label}
            </Button>
          ) : (
            t('no action')
          )}
        </Box>
      </Box>
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '28px',
          height: '28px',
          padding: 0,
          color: config.textColor,
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          opacity: 0.7,
          transition: 'all 0.2s',
          '&:hover': {
            opacity: 1,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: '16px' }} />
      </IconButton>
      
      {/* Progress bar - only show if duration > 0 */}
      {toast.duration && toast.duration > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: `${progress}%`,
              backgroundColor: config.progressColor,
              transition: isPaused ? 'none' : 'width 100ms linear',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ToastNotification;
